import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Layout, PageHero } from "@/components/site/Layout";
import { Seo } from "@/components/site/Seo";
import { Button } from "@/components/ui/button";
import { useI18n, localized, pick } from "@/lib/i18n";
import { useLp } from "@/lib/localePath";
import { fetchPage, fetchService, fetchServices } from "@/lib/queries";
import { resolveImage, banners } from "@/lib/images";
import { fallbackServices, mergeServiceFallbacks, servicePath } from "@/lib/serviceData";
import { ArrowRight, ShieldCheck, Sparkles, Building2, Bug, Trees, Camera, Cctv, Truck, CheckCircle2, HelpCircle, MapPin, ChevronRight } from "lucide-react";
import { getLocalPagesForService } from "@/lib/localSeo";
import { getUseCases, USE_CASES_LABEL } from "@/lib/useCases";
import { COPY } from "@/lib/uiCopy";
import { breadcrumbJsonLd, serviceJsonLd } from "@/lib/seoSchemas";
import { getFAQs, faqJsonLd } from "@/lib/faqData";
import { getServiceSeo } from "@/lib/serviceSeo";

// İlçe (bölge) sayfalarına iç link bloğunu açar/kapatır.
// scripts/generate-sitemap.mjs içindeki INCLUDE_LOCAL_PAGES ile AYNI ANDA değişmeli.
const SHOW_LOCAL_LINKS = false;

const iconMap: Record<string, any> = {
  Shield: ShieldCheck, Sparkles, Building2, Bug, Trees, Camera: Cctv, Cctv, Truck,
};

// use cases moved to src/lib/useCases.ts

export function PhilosophyPage() {
  const lp = useLp();
  const { t, locale } = useI18n();
  const { data } = useQuery({ queryKey: ["page", "felsefemiz"], queryFn: () => fetchPage("felsefemiz") });
  const ownTitle = data ? (data as any)[`title_${locale}`] : "";
  const title = (ownTitle && ownTitle.trim().length > 0)
    ? ownTitle
    : pick(locale, COPY.philosophyTitle);
  const ownContent = data ? (data as any)[`content_${locale}`] : "";
  const dbContent = data ? localized(data, "content", locale) : "";
  // Prefer CMS content for the active locale; if missing, use the in-app translated fallback.
  const fallback = pick(locale, COPY.philosophyFallback);
  const content = (ownContent && ownContent.trim().length > 0)
    ? ownContent
    : (fallback || dbContent);
  const image = resolveImage(data?.image_url ?? null, "felsefemiz");
  return (
    <Layout>
      <Seo
        path="/felsefemiz"
        title={title}
        description={content.slice(0, 160)}
        image={image}
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Ana Sayfa", path: "/" },
            { name: title, path: "/felsefemiz" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "@id": "https://anadoluterra.com/felsefemiz#aboutpage",
            name: title,
            url: "https://anadoluterra.com/felsefemiz",
            inLanguage: locale,
            isPartOf: { "@id": "https://anadoluterra.com/#website" },
            about: { "@id": "https://anadoluterra.com/#organization" },
            mainEntity: { "@id": "https://anadoluterra.com/#organization" },
          },
        ]}
      />
      <PageHero title={title} />
      <article className="container-page py-16 max-w-4xl">
        <div className="prose prose-lg max-w-none prose-headings:font-display prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-10 prose-p:text-foreground/85 prose-p:leading-relaxed">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      </article>
    </Layout>
  );
}

export function ServiceAreasPage() {
  const lp = useLp();
  const { t, locale } = useI18n();
  const { data } = useQuery({ queryKey: ["page", "hizmet-alanlarimiz"], queryFn: () => fetchPage("hizmet-alanlarimiz") });
  const title = data ? localized(data, "title", locale) : pick(locale, COPY.serviceAreasTitle);
  const description = data ? (localized(data, "content", locale) || "").slice(0, 160) : pick(locale, COPY.serviceAreasDesc);
  return (
    <Layout>
      <Seo
        path="/hizmet-alanlarimiz"
        title={title}
        description={description}
        image={resolveImage(data?.image_url, "hizmet-alanlarimiz")}
        jsonLd={breadcrumbJsonLd([
          { name: "Ana Sayfa", path: "/" },
          { name: title, path: "/hizmet-alanlarimiz" },
        ])}
      />
      <PageHero title={title} variant="band" />

      {/* SERVICE AREAS — sectors served (ana sayfadaki layout) */}
      <section className="section-padding bg-ink text-ink-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src={banners[1]} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/40" />
        </div>
        <div className="container-page relative">
          <div className="grid lg:grid-cols-3 gap-12 items-start">
            <div className="lg:sticky lg:top-28">
              <span className="heading-eyebrow text-accent">{pick(locale, COPY.areasEyebrow)}</span>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold">
                {pick(locale, COPY.areasTitle)}
              </h2>
              <p className="mt-4 text-ink-foreground/70">
                {pick(locale, COPY.areasSub)}
              </p>
              <Button asChild className="mt-8 bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent">
                <Link to={lp("/teklif-iste")}>
                  {pick(locale, COPY.areasRequestQuote)} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
              {[COPY.sector1, COPY.sector2, COPY.sector3, COPY.sector4, COPY.sector5, COPY.sector6, COPY.sector7, COPY.sector8].map((a, i) => (
                <div key={i} className="group flex items-center gap-3 rounded-xl border border-ink-foreground/10 bg-ink-foreground/[0.04] px-4 py-4 hover:border-accent/60 hover:bg-ink-foreground/[0.08] hover:-translate-y-0.5 hover:shadow-lg transition-base cursor-default">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-accent/15 text-accent group-hover:bg-accent group-hover:text-accent-foreground group-hover:scale-110 transition-base">
                    <Building2 className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium text-ink-foreground/90 group-hover:text-red-400 transition-base">{pick(locale, a)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {data && localized(data, "content", locale) && (
        <article className="container-page py-16 max-w-4xl">
          <div className="prose prose-lg">
            <ReactMarkdown>{localized(data, "content", locale)}</ReactMarkdown>
          </div>
        </article>
      )}
    </Layout>
  );
}

export function ServicesIndexPage() {
  const lp = useLp();
  const { t, locale } = useI18n();
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: fetchServices });
  const serviceItems = mergeServiceFallbacks(services && services.length > 0 ? services : fallbackServices);
  const seoTitle = pick(locale, COPY.servicesIndexSeoTitle);
  const seoDesc = pick(locale, COPY.servicesIndexSeoDesc);
  return (
    <Layout>
      <Seo
        path="/hizmetlerimiz"
        title={seoTitle}
        description={seoDesc}
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Ana Sayfa", path: "/" },
            { name: t("nav.services"), path: "/hizmetlerimiz" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Anadolu Terra Hizmetleri",
            itemListElement: serviceItems.map((s: any, i: number) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://anadoluterra.com${servicePath(s.slug)}`,
              name: localized(s, "title", locale),
            })),
          },
        ]}
      />
      <PageHero title={t("nav.services")} subtitle={t("section.services.subtitle")} variant="band" />
      <section className="py-10 md:py-14 bg-background">
        <div className="container-page max-w-3xl text-center">
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            {pick(locale, COPY.servicesIndexIntro)}
          </p>
        </div>
      </section>
      <section className="section-padding pt-6 md:pt-8 bg-secondary/40">
        <div className="container-page">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceItems.map((s: any) => {
              const Icon = iconMap[s.icon || ""] ?? ShieldCheck;
              const cases = getUseCases(s.slug, locale);
              return (
                <Link
                  key={s.id}
                  to={lp(servicePath(s.slug))}
                  className="group relative flex flex-col rounded-2xl bg-card border border-border shadow-card hover:shadow-elevated hover:-translate-y-1 transition-smooth overflow-hidden"
                >
                  <div className="h-1 w-full bg-gradient-to-r from-primary via-accent to-primary" />
                  <div className="p-6 flex flex-col h-full">
                    <div className="flex items-start gap-4">
                      <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-accent text-accent-foreground shadow-accent shrink-0">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="min-w-0">
                        <h2 className="font-display text-lg font-semibold text-primary group-hover:text-accent transition-base">
                          {localized(s, "title", locale)}
                        </h2>
                        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{localized(s, "summary", locale)}</p>
                      </div>
                    </div>
                    <div className="mt-5 pt-5 border-t border-border">
                      <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-accent">
                        {USE_CASES_LABEL[locale]}
                      </span>
                      <ul className="mt-3 space-y-2">
                        {cases.map((c) => (
                          <li key={c} className="flex items-start gap-2 text-sm text-foreground/85">
                            <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:text-accent transition-base">
                      {t("common.examine")} <ArrowRight className="h-4 w-4 transition-base group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
}

const serviceIconMap: Record<string, any> = {
  "koruma-ve-guvenlik": ShieldCheck,
  "zirhli-arac-ve-tasima": Truck,
  "genel-temizlik": Sparkles,
  "tesis-yonetimi": Building2,
  "ilaclama": Bug,
  "elektronik-guvenlik-sistemleri": Cctv,
};

export function ServiceDetailPage() {
  const lp = useLp();
  const params = useParams();
  const location = useLocation();
  // /hizmet/:slug rotasından params.slug; /koruma-ve-guvenlik gibi doğrudan URL'lerden pathname
  const slug = params.slug ?? location.pathname.replace(/^\/+/, "").split("/")[0];
  const { t, locale } = useI18n();
  const { data } = useQuery({ queryKey: ["service", slug], queryFn: () => fetchService(slug!), enabled: !!slug });
  const rawService = data ?? fallbackServices.find((item) => item.slug === slug);
  const service = rawService ? mergeServiceFallbacks([rawService])[0] : undefined;
  if (!service) return <Layout><div className="container-page py-20" /></Layout>;
  const Icon = serviceIconMap[service.slug] ?? ShieldCheck;
  const sTitle = localized(service, "title", locale);
  const sSummary = localized(service, "summary", locale) || "";
  const faqs = getFAQs(service.slug, locale);
  const seo = getServiceSeo(service.slug, locale as any);
  const seoTitle = seo?.seoTitle || sTitle;
  const seoDesc = seo?.seoDescription || sSummary;
  return (
    <Layout>
      <Seo
        path={`/hizmet/${service.slug}`}
        title={seoTitle}
        description={seoDesc}
        jsonLd={[
          serviceJsonLd({
            slug: service.slug,
            name: seoTitle,
            description: seoDesc,
            serviceType: sTitle,
            offers: seo?.offers,
            areaServed: seo?.areaServed,
          }),
          breadcrumbJsonLd([
            { name: "Ana Sayfa", path: "/" },
            { name: "Hizmetlerimiz", path: "/hizmetlerimiz" },
            { name: sTitle, path: `/hizmet/${service.slug}` },
          ]),
          ...(faqs.length > 0 ? [faqJsonLd(faqs)] : []),
        ]}
      />
      <PageHero title={sTitle} subtitle={sSummary} variant="band" />
      <article className="container-page py-16 max-w-4xl">
        <div className="mb-12 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-3xl bg-gradient-accent opacity-20 blur-2xl" aria-hidden />
            <div className="relative h-32 w-32 md:h-40 md:w-40 rounded-3xl border-2 border-accent/40 bg-secondary/40 flex items-center justify-center shadow-elevated">
              <Icon className="h-16 w-16 md:h-20 md:w-20 text-accent" strokeWidth={1.25} aria-hidden />
            </div>
          </div>
        </div>
        <div className="prose prose-lg max-w-none prose-headings:font-display prose-headings:text-primary prose-strong:text-primary prose-p:text-foreground/85 prose-li:text-foreground/85">
          <ReactMarkdown>{localized(service, "content", locale)}</ReactMarkdown>
        </div>

        {faqs.length > 0 && (
          <section className="mt-16">
            <div className="flex items-center gap-3 mb-6">
              <HelpCircle className="h-6 w-6 text-accent" />
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary m-0">
                {pick(locale, COPY.faqEyebrow)}
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-5 shadow-card open:shadow-elevated transition-smooth">
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-display text-base md:text-lg font-semibold text-primary">
                    <span>{f.q}</span>
                    <span className="text-accent text-2xl leading-none transition-transform group-open:rotate-45 shrink-0">+</span>
                  </summary>
                  <p className="mt-3 text-sm md:text-base text-foreground/80 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Bölge sayfalarına iç link — yalnızca TR. Bu blok olmadan ilçe sayfaları
            sitemap'te var ama siteden linksiz kalır; Google linksiz sayfayı zayıf sayar.
            ⚠️ 2026-08-03: KAPALI. İlçe sayfaları nginx'te 301'lendi ve sitemap'ten
            çıkarıldı (bkz. scripts/generate-sitemap.mjs INCLUDE_LOCAL_PAGES). Bu blok
            açık kalırsa hizmet sayfası her taranışında 8 adet 301'e giden link sunar;
            tarama bütçesi boşa gider ve sinyal birleşmesi yavaşlar.
            Geri açarken generate-sitemap.mjs, nginx.conf ve bu bayrak BİRLİKTE açılmalı. */}
        {SHOW_LOCAL_LINKS && locale === "tr" && getLocalPagesForService(service.slug).length > 0 && (
          <section className="mt-16">
            <div className="flex items-center gap-3 mb-5">
              <MapPin className="h-6 w-6 text-accent" />
              <h2 className="font-display text-2xl md:text-3xl font-bold text-primary m-0">
                Hizmet verdiğimiz bölgeler
              </h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Bölgeye özel çalışma düzenimizi ve sık sorulan soruları ilçe sayfalarında bulabilirsiniz.
            </p>
            <ul className="grid gap-2 sm:grid-cols-2">
              {getLocalPagesForService(service.slug).map((p) => (
                <li key={p.bolge}>
                  <Link
                    to={`/hizmet/${p.hizmet}/${p.bolge}`}
                    className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium text-primary shadow-card hover:border-accent/50 hover:shadow-elevated transition-smooth"
                  >
                    <span>{p.h1}</span>
                    <ChevronRight className="h-4 w-4 text-accent shrink-0" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-12 rounded-2xl bg-secondary/60 p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-xl font-semibold text-primary">{t("section.cta.title")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("section.cta.subtitle")}</p>
          </div>
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent shrink-0">
            <Link to={lp("/teklif-iste")}>{t("common.requestQuote")}</Link>
          </Button>
        </div>
      </article>
    </Layout>
  );
}
